import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardSidebar from '@/components/feature/DashboardSidebar';
import DashboardTopBar from '@/components/feature/DashboardTopBar';
import StatusBadge from '@/components/base/StatusBadge';
import { VENDOR_ORDERS } from '@/mocks/vendor';
import type { Order } from '@/mocks/vendor';
import type { Product } from '@/mocks/admin';
import VendorCharts from './components/VendorCharts';
import { useProductsStore } from '@/hooks/useProductsStore';
import { useNotificationsStore, saveLastSeenDate } from '@/hooks/useNotificationsStore';
import { useAuth } from '@/contexts/AuthContext';
import { apiRequest } from '@/lib/api';
import type { AuthUser } from '@/lib/api';
import { PRODUCT_CATEGORIES } from '@/constants/productCategories';
import type { Notification } from '@/mocks/shared';

type Tab = 'overview' | 'products' | 'orders' | 'notifications' | 'add' | 'profile';

const VENDOR_TAB_PATH_MAP: Record<Tab, string> = {
  overview: '/vendor',
  products: '/vendor/products',
  orders: '/vendor/orders',
  notifications: '/vendor/notifications',
  add: '/vendor/products',
  profile: '/vendor/profile',
};

const getVendorTabFromPath = (pathname: string): Tab => {
  if (pathname.includes('/products')) return 'products';
  if (pathname.includes('/orders')) return 'orders';
  if (pathname.includes('/notifications')) return 'notifications';
  if (pathname.includes('/profile')) return 'profile';
  return 'overview';
};
type Step = 1 | 2 | 3;

const formatPrice = (n: number) => `${n.toLocaleString('fr-FR')} FCFA`;
const formatDate = (d: string) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const orderStatusColor: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700', confirmed: 'bg-teal-100 text-teal-700',
  shipped: 'bg-navy/10 text-navy', delivered: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-gray-100 text-gray-600',
};
const orderStatusLabel: Record<string, string> = {
  pending: '🟡 En attente', confirmed: '✅ Confirmé', shipped: '📦 Expédié',
  delivered: '🟢 Livré', cancelled: '⚫ Annulé',
};
const orderDisplayStatus = (order: Order) => {
  const paymentStatus = order.paymentStatus ?? 'completed';

  if (paymentStatus !== 'completed') {
    return {
      color: 'bg-amber-100 text-amber-700',
      label: 'Paiement non validé',
    };
  }

  return {
    color: orderStatusColor[order.status],
    label: orderStatusLabel[order.status],
  };
};

const PRODUCT_BADGES = ['Bestseller', 'Nouveau', 'Promo', 'Coup de coeur'];
const PRODUCT_FALLBACK_IMAGE = 'https://readdy.ai/api/search-image?query=product%20item%20clean%20white%20background%20minimal&width=300&height=300&seq=vendor-product-fallback&orientation=squarish';

const cleanProfileValue = (value?: string | null) => (value ?? '').trim();

const professionalShopName = (user?: AuthUser | null) => {
  const shopName = cleanProfileValue(user?.vendor_application?.shop_name);
  const locationValues = [
    user?.vendor_application?.location,
    user?.paroisse,
    user?.diocese_prefere,
  ].map(cleanProfileValue).filter(Boolean);

  if (!shopName) return '';

  return locationValues.some(value => value.toLowerCase() === shopName.toLowerCase()) ? '' : shopName;
};

const profileFromUser = (user?: AuthUser | null) => ({
  name: user?.name ?? 'Vendeur',
  email: user?.email ?? '',
  phone: user?.phone ?? '',
  shopName: professionalShopName(user),
  bio: user?.vendor_application?.bio ?? '',
  location: user?.vendor_application?.location ?? user?.paroisse ?? user?.diocese_prefere ?? '',
  category: user?.vendor_application?.category ?? '',
  avatarPreview: user?.avatar ?? '',
});

const DAKAR_COMMUNES = [
  'Plateau', 'Médina', 'Grand Dakar', 'Fann – Point E – Amitié', 'Liberté',
  'Dieuppeul – Derklé', 'HLM', 'Biscuiterie', 'Gueule Tapée – Fass – Colobane',
  'Parcelles Assainies', 'Yoff', 'Almadies', 'Ngor', 'Ouakam', 'Mermoz – Sacré-Coeur',
  'Gorée', 'Pikine', 'Guédiawaye', 'Rufisque', 'Thiaroye', 'Mbao', 'Bargny',
  'Diamniadio', 'Sébikhotane', 'Sangalkam',
];

interface FormData {
  name: string; category: string; price: string; stock: string; description: string;
  condition: string; badge: string; deliveryDelay: string; location: string; deliveryFee: string; weight: string;
  images: string[];
}

const emptyForm: FormData = { name: '', category: '', price: '', stock: '', description: '', condition: 'Neuf', badge: '', deliveryDelay: '', location: '', deliveryFee: '', weight: '', images: [] };

type VendorOrder = Order & {
  commandeId: string;
  itemIndex: number;
};

const handleProductImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
  event.currentTarget.onerror = null;
  event.currentTarget.src = PRODUCT_FALLBACK_IMAGE;
};

export default function VendorDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUser } = useAuth();
  const [tab, setTabState] = useState<Tab>(() => getVendorTabFromPath(location.pathname));
  // ── Products shared store ──
  const { addProduct, updateProduct, deleteProduct, restoreProduct, products: allProducts } = useProductsStore();
  const products = allProducts;

  // ── Vendor notifications store ──
  const { notifications: vendorNotifs, unreadCount: vendorUnreadCount, markAsRead: markVendorAsRead, markAllAsRead: markVendorAllRead, newSinceLastSeen: newVendorNotifs } = useNotificationsStore('vendor');
  const [newNotifToast, setNewNotifToast] = useState<string | null>(null);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);

  const [orders, setOrders] = useState<VendorOrder[]>([]);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [editForm, setEditForm] = useState<Partial<FormData>>({});
  const [editErrors, setEditErrors] = useState<Partial<Record<string, string>>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'deleted'>('all');
  const [orderSearch, setOrderSearch] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState<'all' | 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled'>('all');
  const [selectedOrder, setSelectedOrder] = useState<VendorOrder | null>(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [toasts, setToasts] = useState<{ id: number; message: string; type: 'success' | 'info' | 'error' }[]>([]);

  const setTab = (newTab: Tab) => {
    setTabState(newTab);
    if (newTab !== 'add') navigate(VENDOR_TAB_PATH_MAP[newTab]);
  };

  // Sync URL → tab when sidebar navigation happens
  useEffect(() => {
    const urlTab = getVendorTabFromPath(location.pathname);
    setTabState(urlTab);
  }, [location.pathname]);

  // Toast for new notifications from admin
  useEffect(() => {
    if (newVendorNotifs.length > 0) {
      const first = newVendorNotifs[0];
      setNewNotifToast(first.type === 'approval'
        ? `Produit approuvé — ${first.message}`
        : `Produit rejeté — voir les notifications`
      );
      const t = setTimeout(() => setNewNotifToast(null), 5000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [newVendorNotifs]);

  // Auto mark-as-read when notifications tab is open
  useEffect(() => {
    if (tab === 'notifications' && vendorUnreadCount > 0) {
      const t = setTimeout(() => { markVendorAllRead(); saveLastSeenDate('vendor'); }, 600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [markVendorAllRead, tab, vendorUnreadCount]);

  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const profileFileRef = useRef<HTMLInputElement>(null);

  interface ProfileData {
    name: string; email: string; phone: string; shopName: string;
    bio: string; location: string; category: string; avatarPreview: string;
  }
  const [profile, setProfile] = useState<ProfileData>({
    ...profileFromUser(user),
  });
  const [profileAvatarFile, setProfileAvatarFile] = useState<File | null>(null);
  const [profileErrors, setProfileErrors] = useState<Partial<Record<keyof ProfileData, string>>>({});
  const [profileSaving, setProfileSaving] = useState(false);

  useEffect(() => {
    if (!user) return;

    setProfile(profileFromUser(user));
  }, [user]);

  useEffect(() => {
    const loadVendorOrders = async () => {
      try {
        const response = await apiRequest<Array<{
          id: string;
          commande_id: number;
          item_index: number;
          product_id: number;
          product_name: string;
          product_image?: string | null;
          buyer: string;
          buyer_phone?: string | null;
          quantity: number;
          total: number;
          status: Order['status'];
          payment_status?: string | null;
          date: string;
          address?: string | null;
        }>>('/vendor/orders');

        setOrders(response.map((order) => ({
          id: order.id,
          commandeId: String(order.commande_id),
          itemIndex: order.item_index,
          productId: String(order.product_id),
          productName: order.product_name,
          productImage: order.product_image || 'https://readdy.ai/api/search-image?query=product%20item%20clean%20white%20background%20minimal&width=80&height=80&seq=vendor-order-fallback&orientation=squarish',
          buyer: order.buyer,
          phone: order.buyer_phone || undefined,
          quantity: order.quantity,
          total: Number(order.total ?? 0),
          status: order.status,
          paymentStatus: order.payment_status || 'completed',
          date: order.date,
          address: order.address || 'Non renseignée',
        })));
      } catch (error) {
        console.error('Vendor orders error:', error);
        setOrders(VENDOR_ORDERS.map((order) => ({
          ...order,
          commandeId: order.id,
          itemIndex: 0,
        })));
      }
    };

    void loadVendorOrders();
  }, []);

  const setProfileField = (k: keyof ProfileData, v: string) =>
    setProfile(f => ({ ...f, [k]: v }));

  const handleProfileAvatar = (files: FileList | null) => {
    if (!files || !files[0]) return;
    setProfileAvatarFile(files[0]);
    const url = URL.createObjectURL(files[0]);
    setProfileField('avatarPreview', url);
  };

  const handleSaveProfile = async () => {
    const e: Partial<Record<keyof ProfileData, string>> = {};
    if (profile.name.length < 2) e.name = 'Minimum 2 caractères';
    if (!/^\S+@\S+\.\S+/.test(profile.email)) e.email = 'Email invalide';
    if (profile.phone.length < 8) e.phone = 'Numéro invalide';
    if (profile.shopName.length < 2) e.shopName = 'Minimum 2 caractères';
    if (Object.keys(e).length > 0) { setProfileErrors(e); return; }
    setProfileErrors({});
    setProfileSaving(true);

    try {
      const body = new FormData();
      body.append('_method', 'PUT');
      body.append('name', profile.name);
      body.append('email', profile.email);
      body.append('phone', profile.phone);
      body.append('paroisse', profile.location);
      body.append('diocese_prefere', profile.location);
      body.append('structure_name', profile.shopName);
      body.append('category', profile.category);
      body.append('bio', profile.bio);
      if (profileAvatarFile) body.append('avatar_file', profileAvatarFile);

      const updatedUser = await apiRequest<NonNullable<typeof user>>('/user', {
        method: 'POST',
        body,
      });

      updateUser(updatedUser);
      setProfileAvatarFile(null);
      setProfile((current) => ({ ...current, avatarPreview: updatedUser.avatar ?? current.avatarPreview }));
      addToast('Profil mis à jour avec succès', 'success');
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Mise à jour du profil impossible', 'error');
    } finally {
      setProfileSaving(false);
    }
  };

  const paidOrders = useMemo(
    () => orders.filter((order) => (order.paymentStatus ?? 'completed') === 'completed'),
    [orders]
  );

  const salesMonthly = useMemo(() => (
    Array.from({ length: 7 }, (_, index) => {
      const date = new Date();
      date.setMonth(date.getMonth() - (6 - index));
      const month = date.toLocaleDateString('fr-FR', { month: 'short' });
      const matchingOrders = paidOrders.filter((order) => {
        const orderDate = new Date(order.date);
        return orderDate.getMonth() === date.getMonth() && orderDate.getFullYear() === date.getFullYear();
      });

      return {
        month: month.charAt(0).toUpperCase() + month.slice(1, 3),
        ventes: matchingOrders.reduce((sum, order) => sum + order.total, 0),
        commandes: matchingOrders.length,
      };
    })
  ), [paidOrders]);

  const topProducts = useMemo(() => {
    const revenues = paidOrders.reduce<Record<string, number>>((accumulator, order) => {
      accumulator[order.productName] = (accumulator[order.productName] ?? 0) + order.total;
      return accumulator;
    }, {});

    return Object.entries(revenues)
      .sort(([, left], [, right]) => right - left)
      .slice(0, 5)
      .map(([name, revenus]) => ({ name, revenus }));
  }, [paidOrders]);

  const orderStatusDist = useMemo(() => ([
    { name: 'En attente', value: orders.filter((order) => order.status === 'pending').length, fill: '#C9A84C' },
    { name: 'Confirmées', value: orders.filter((order) => order.status === 'confirmed').length, fill: '#10B981' },
    { name: 'Expédiées', value: orders.filter((order) => order.status === 'shipped').length, fill: '#1B2A4A' },
    { name: 'Livrées', value: orders.filter((order) => order.status === 'delivered').length, fill: '#14B8A6' },
    { name: 'Annulées', value: orders.filter((order) => order.status === 'cancelled').length, fill: '#EF4444' },
  ]), [orders]);

  const totalSales = useMemo(() => paidOrders.reduce((sum, order) => sum + order.total, 0), [paidOrders]);
  const currentMonthSales = useMemo(() => {
    const now = new Date();
    return paidOrders
      .filter((order) => {
        const orderDate = new Date(order.date);
        return orderDate.getMonth() === now.getMonth() && orderDate.getFullYear() === now.getFullYear();
      })
      .reduce((sum, order) => sum + order.total, 0);
  }, [paidOrders]);

  const setField = (k: keyof FormData, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const handleImageFiles = (files: FileList | null) => {
    if (!files) return;
    const remaining = 5 - imagePreviews.length;
    if (remaining <= 0) return;
    const selected = Array.from(files).slice(0, remaining);
    const newPreviews = selected.map((file) => URL.createObjectURL(file));
    setImageFiles((prev) => [...prev, ...selected]);
    setImagePreviews((prev) => [...prev, ...newPreviews]);
    setForm((f) => ({ ...f, images: [...f.images, ...selected.map((file) => file.name)] }));
  };

  const removeImage = (index: number) => {
    setImagePreviews((prev) => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== index) }));
  };

  const validateStep1 = () => {
    const e: typeof errors = {};
    if (form.name.length < 3) e.name = 'Minimum 3 caractères';
    if (!form.category) e.category = 'Requis';
    if (!form.price || Number(form.price) <= 0) e.price = 'Prix doit être > 0';
    if (form.stock === '' || Number(form.stock) < 0) e.stock = 'Stock ≥ 0';
    if (!form.description.trim()) e.description = 'Requis';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e: typeof errors = {};
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleSubmit = () => {
    const newProduct: Product & { imageFile?: File | null } = {
      id: `vp-${Date.now()}`,
      name: form.name,
      category: form.category,
      price: Number(form.price),
      stock: Number(form.stock),
      description: form.description,
      images: imagePreviews.length > 0
        ? imagePreviews
        : ['https://readdy.ai/api/search-image?query=product%20item%20clean%20white%20background%20minimal&width=300&height=300&seq=new1&orientation=squarish'],
      badge: form.badge || undefined,
      condition: form.condition as 'Neuf' | 'Occasion',
      deliveryDelay: form.deliveryDelay || undefined,
      location: form.location || undefined,
      deliveryFee: 'Gratuit',
      status: 'pending',
      vendorId: 'vendor-1',
      vendorName: user?.name ?? 'Vendeur',
      createdAt: new Date().toISOString(),
      imageFile: imageFiles[0] ?? null,
    };
    addProduct(newProduct);
    addToast('Produit soumis — en attente de validation admin', 'success');
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setStep(1);
      setForm(emptyForm);
      setImagePreviews([]);
      setImageFiles([]);
      setTab('products');
    }, 2500);
  };

  const filteredProducts = products.filter((p) => {
    const matchSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const statusCounts = {
    all: products.length,
    approved: products.filter(p => p.status === 'approved').length,
    pending: products.filter(p => p.status === 'pending').length,
    rejected: products.filter(p => p.status === 'rejected').length,
    deleted: products.filter(p => p.status === 'deleted').length,
  };

  const filteredOrders = orders.filter((o) => {
    const matchSearch =
      o.productName.toLowerCase().includes(orderSearch.toLowerCase()) ||
      o.buyer.toLowerCase().includes(orderSearch.toLowerCase()) ||
      o.id.toLowerCase().includes(orderSearch.toLowerCase());
    const matchStatus = orderStatusFilter === 'all' || o.status === orderStatusFilter;
    return matchSearch && matchStatus;
  });

  const orderCounts = {
    all: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    confirmed: orders.filter(o => o.status === 'confirmed').length,
    shipped: orders.filter(o => o.status === 'shipped').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    cancelled: orders.filter(o => o.status === 'cancelled').length,
  };
  const approvalRate = products.length === 0 ? 0 : Math.round((statusCounts.approved / products.length) * 100);
  const bestMonth = salesMonthly.reduce((best, current) => current.ventes > best.ventes ? current : best, salesMonthly[0] ?? { month: '-', ventes: 0, commandes: 0 });
  const topProduct = topProducts[0];

  const statsCards = [
    { label: 'Revenus totaux', value: formatPrice(totalSales), icon: 'ri-money-cny-circle-line', color: 'from-gold to-gold-dark', sub: `+${formatPrice(currentMonthSales)} ce mois`, subColor: 'text-emerald-600' },
    { label: 'Commandes totales', value: orders.length, icon: 'ri-shopping-bag-line', color: 'from-emerald-500 to-teal-600', sub: `${orderCounts.pending} en attente`, subColor: 'text-amber-600' },
    { label: 'Produits actifs', value: statusCounts.approved, icon: 'ri-store-line', color: 'from-navy to-navy-lighter', sub: `${statusCounts.pending} en attente`, subColor: 'text-amber-600' },
    { label: 'Taux approbation', value: `${approvalRate}%`, icon: 'ri-star-line', color: 'from-gold-dark to-[#a07828]', sub: `${statusCounts.rejected} rejeté(s)`, subColor: 'text-gray-500' },
  ];

  const chartHighlights = [
    { label: 'Meilleur mois', value: bestMonth.month, sub: formatPrice(bestMonth.ventes), icon: 'ri-trophy-line', color: 'from-gold to-gold-dark' },
    { label: 'Commandes ce mois', value: String(salesMonthly[salesMonthly.length - 1]?.commandes ?? 0), sub: formatPrice(currentMonthSales), icon: 'ri-shopping-bag-line', color: 'from-emerald-500 to-teal-600' },
    { label: 'Produit top', value: topProduct?.name ?? 'Aucun', sub: topProduct ? formatPrice(topProduct.revenus) : 'Pas encore de ventes', icon: 'ri-star-line', color: 'from-navy to-navy-lighter' },
    { label: 'Taux approbation', value: `${approvalRate}%`, sub: `${statusCounts.approved}/${products.length || 0} produits`, icon: 'ri-heart-line', color: 'from-gold-dark to-[#a07828]' },
  ];

  const handleUpdateOrderStatus = async (newStatus: Order['status']) => {
    if (!selectedOrder) return;
    setStatusUpdateLoading(true);
    try {
      if (selectedOrder.paymentStatus && selectedOrder.paymentStatus !== 'completed') {
        addToast('Paiement non validé — livraison indisponible', 'error');
        return;
      }

      await apiRequest(`/vendor/orders/${selectedOrder.commandeId}/${selectedOrder.itemIndex}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });

      const updated = { ...selectedOrder, status: newStatus };
      setOrders(prev => prev.map(o => o.id === selectedOrder.id ? updated : o));
      setSelectedOrder(updated);
      const statusLabels: Record<string, string> = {
        confirmed: 'Commande confirmée',
        shipped: 'Colis expédié',
        delivered: 'Commande livrée',
        cancelled: 'Commande annulée',
      };
      addToast(statusLabels[newStatus] || 'Statut mis à jour', newStatus === 'cancelled' ? 'error' : 'success');
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Mise à jour impossible', 'error');
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const addToast = (message: string, type: 'success' | 'info' | 'error' = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  };

  const getNextStatuses = (status: Order['status']): { key: Order['status']; label: string; icon: string; color: string }[] => {
    if (status === 'delivered' || status === 'cancelled') {
      return [];
    }

    return [
      { key: 'delivered', label: 'Marquer livré', icon: 'ri-map-pin-2-line', color: 'bg-emerald-500 hover:bg-emerald-600 text-white' },
    ];
  };

  const handleDeleteProduct = async (id: string) => {
    await deleteProduct(id);
    setDeleteConfirmId(null);
    addToast('Produit déplacé dans la corbeille', 'success');
  };

  const handleRestoreProduct = async (id: string) => {
    await restoreProduct(id);
    addToast('Produit restauré', 'success');
  };

  const handleOpenEdit = (product: Product) => {
    setEditProduct(product);
    setEditForm({
      name: product.name,
      category: product.category,
      price: String(product.price),
      stock: String(product.stock),
      description: product.description,
      badge: product.badge ?? '',
      condition: product.condition,
      deliveryDelay: product.deliveryDelay,
      location: product.location,
      deliveryFee: product.deliveryFee === 'Gratuit' ? '' : String(product.deliveryFee),
      weight: '',
    });
    setEditErrors({});
  };

  const handleSaveEdit = () => {
    const e: Record<string, string> = {};
    if (!editForm.name || editForm.name.length < 3) e.name = 'Minimum 3 caractères';
    if (!editForm.category) e.category = 'Requis';
    if (!editForm.price || Number(editForm.price) <= 0) e.price = 'Prix doit être > 0';
    if (editForm.stock === '' || Number(editForm.stock) < 0) e.stock = 'Stock ≥ 0';
    if (!editForm.description?.trim()) e.description = 'Requis';
    if (Object.keys(e).length > 0) { setEditErrors(e); return; }

    updateProduct(editProduct!.id, {
      name: editForm.name!,
      category: editForm.category!,
      price: Number(editForm.price),
      stock: Number(editForm.stock),
      description: editForm.description!,
      badge: editForm.badge || undefined,
      condition: editForm.condition as 'Neuf' | 'Occasion',
      deliveryDelay: editForm.deliveryDelay || undefined,
      location: editForm.location || undefined,
      deliveryFee: 'Gratuit',
      status: 'pending' as const,
    });
    setEditProduct(null);
    addToast('Produit modifié — en attente de validation', 'success');
  };

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-cream md:flex-row">
      <DashboardSidebar
        role="vendor"
        userName={profile.name}
        userAvatar={profile.avatarPreview}
        badgeCounts={{ '/vendor/notifications': vendorUnreadCount }}
      />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <DashboardTopBar title="Dashboard Vendeur" subtitle={`Bienvenue, ${profile.name}`} userName={profile.name} userAvatar={profile.avatarPreview} notificationRole="vendor" />

        {/* New notification toast */}
        {newNotifToast && (
          <div className="fixed top-5 right-5 z-50 max-w-sm w-full">
            <div className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-sm font-medium ${newNotifToast.includes('approuvé') ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${newNotifToast.includes('approuvé') ? 'bg-emerald-100' : 'bg-red-100'}`}>
                <i className={newNotifToast.includes('approuvé') ? 'ri-checkbox-circle-line text-emerald-600' : 'ri-close-circle-line text-red-500'}></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-xs mb-0.5">{newNotifToast.includes('approuvé') ? 'Produit approuvé !' : 'Produit rejeté'}</p>
                <p className="text-xs opacity-80 leading-relaxed whitespace-pre-line break-words">{newNotifToast}</p>
              </div>
              <button onClick={() => setNewNotifToast(null)} className="flex-shrink-0 w-5 h-5 flex items-center justify-center opacity-60 hover:opacity-100 cursor-pointer"><i className="ri-close-line text-sm"></i></button>
            </div>
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-4 space-y-6 sm:p-6">
          {/* OVERVIEW */}
          {tab === 'overview' && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {statsCards.map((c, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 border border-gray-100">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${c.color} mb-3`}>
                      <i className={`${c.icon} text-white text-lg`}></i>
                    </div>
                    <p className="text-xs text-gray-500 mb-1">{c.label}</p>
                    <p className="text-xl font-bold text-gray-900">{c.value}</p>
                    <p className={`text-xs mt-1 ${c.subColor}`}>{c.sub}</p>
                  </div>
                ))}
              </div>
              <VendorCharts
                salesMonthly={salesMonthly}
                topProducts={topProducts}
                orderStatusDist={orderStatusDist}
                highlights={chartHighlights}
              />
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <h3 className="text-sm font-bold text-gray-900 mb-4">Commandes récentes</h3>
                  <div className="space-y-3">
                    {orders.slice(0, 4).map((o) => (
                      <div key={o.id} className="flex items-center gap-3">
                        <img src={o.productImage} alt={o.productName} onError={handleProductImageError} className="w-10 h-10 rounded-lg object-cover bg-gray-100 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{o.productName}</p>
                          <p className="text-xs text-gray-400">{o.buyer} · {formatDate(o.date)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-gray-900">{formatPrice(o.total)}</p>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${orderDisplayStatus(o).color}`}>{orderDisplayStatus(o).label}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <h3 className="text-sm font-bold text-gray-900 mb-4">Mes produits</h3>
                  <div className="space-y-3">
                    {products.map((p) => (
                      <div key={p.id} className="flex items-center gap-3">
                        <img src={p.images[0]} alt={p.name} onError={handleProductImageError} className="w-10 h-10 rounded-lg object-cover bg-gray-100 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{p.name}</p>
                          <p className="text-xs text-gray-400">{formatPrice(p.price)} · Stock: {p.stock}</p>
                        </div>
                        <StatusBadge status={p.status} />
                        {p.status === 'deleted' ? (
                          <button
                            onClick={() => void handleRestoreProduct(p.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-emerald-600 hover:bg-emerald-50 cursor-pointer"
                            title="Restaurer"
                            aria-label={`Restaurer ${p.name}`}
                          >
                            <i className="ri-arrow-go-back-line text-sm"></i>
                          </button>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirmId(p.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-50 hover:text-red-500 cursor-pointer"
                            title="Mettre dans la corbeille"
                            aria-label={`Supprimer ${p.name}`}
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* PRODUCTS */}
          {tab === 'products' && (
            <div className="space-y-4">
              {/* Stats rapides */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                {([
                  { key: 'all', label: 'Tous', color: 'bg-navy text-white', inactive: 'bg-white text-navy border border-gray-100' },
                  { key: 'approved', label: 'Validés par l’admin', color: 'bg-emerald-500 text-white', inactive: 'bg-white text-emerald-600 border border-gray-100' },
                  { key: 'pending', label: 'En attente de validation', color: 'bg-gold text-white', inactive: 'bg-white text-gold-dark border border-gray-100' },
                  { key: 'rejected', label: 'Rejetés', color: 'bg-red-500 text-white', inactive: 'bg-white text-red-500 border border-gray-100' },
                  { key: 'deleted', label: 'Corbeille', color: 'bg-slate-600 text-white', inactive: 'bg-white text-slate-500 border border-gray-100' },
                ] as const).map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setStatusFilter(s.key)}
                    className={`rounded-xl px-3 py-3 text-left transition-all cursor-pointer ${statusFilter === s.key ? s.color : s.inactive}`}
                  >
                    <p className={`text-xl font-bold leading-tight ${statusFilter === s.key ? 'text-white' : ''}`}>{statusCounts[s.key]}</p>
                    <p className={`text-xs mt-0.5 whitespace-nowrap ${statusFilter === s.key ? 'text-white/80' : 'text-gray-500'}`}>{s.label}</p>
                  </button>
                ))}
              </div>

              <div className="bg-white rounded-xl border border-gray-100">
                {/* Header : recherche + bouton ajouter */}
                <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
                  <div className="flex-1 relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                      <i className="ri-search-line text-sm"></i>
                    </div>
                    <input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Rechercher par nom ou catégorie..."
                      className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    )}
                  </div>
                  <button onClick={() => setTab('add')} className="flex items-center gap-1.5 px-4 py-2 bg-navy text-white text-sm rounded-xl hover:bg-navy-lighter cursor-pointer whitespace-nowrap">
                    <i className="ri-add-line"></i>Ajouter
                  </button>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-50">
                        {['Produit','Prix','Stock','État','Statut','Actions'].map(h =>
                          <th key={h} className="text-left text-xs font-semibold text-gray-400 px-5 py-3">{h}</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-5 py-12 text-center">
                            <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
                              <i className="ri-search-line text-gray-400 text-xl"></i>
                            </div>
                            <p className="text-sm font-semibold text-gray-500">Aucun produit trouvé</p>
                            <p className="text-xs text-gray-400 mt-1">Essayez un autre nom ou changez le filtre</p>
                          </td>
                        </tr>
                      ) : (
                        filteredProducts.map((p) => (
                          <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-3">
                                <img src={p.images[0]} alt={p.name} onError={handleProductImageError} className="w-10 h-10 rounded-lg object-cover bg-gray-100 flex-shrink-0"/>
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">{
                                    searchQuery
                                      ? p.name.split(new RegExp(`(${searchQuery})`, 'gi')).map((part, i) =>
                                          part.toLowerCase() === searchQuery.toLowerCase()
                                            ? <mark key={i} className="bg-gold-100 text-gold-dark rounded px-0.5">{part}</mark>
                                            : part
                                        )
                                      : p.name
                                  }</p>
                                  <p className="text-xs text-gray-400">{p.category}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900">{formatPrice(p.price)}</td>
                            <td className="px-4 py-3"><span className={`text-sm font-semibold ${p.stock === 0 ? 'text-red-500' : 'text-gray-700'}`}>{p.stock}</span></td>
                            <td className="px-4 py-3 text-sm text-gray-600">{p.condition}</td>
                            <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                {p.status === 'deleted' ? (
                                  <button onClick={() => void handleRestoreProduct(p.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-emerald-50 text-emerald-600 cursor-pointer" title="Restaurer">
                                    <i className="ri-arrow-go-back-line text-sm"></i>
                                  </button>
                                ) : (
                                  <>
                                    <button onClick={() => handleOpenEdit(p)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gold-50 text-gold-dark cursor-pointer" title="Modifier">
                                      <i className="ri-edit-line text-sm"></i>
                                    </button>
                                    <button onClick={() => setDeleteConfirmId(p.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-400 cursor-pointer" title="Mettre dans la corbeille">
                                      <i className="ri-delete-bin-line text-sm"></i>
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Footer info */}
                {filteredProducts.length > 0 && (
                  <div className="px-5 py-3 border-t border-gray-50">
                    <p className="text-xs text-gray-400">
                      {filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''} affiché{filteredProducts.length > 1 ? 's' : ''}
                      {statusFilter !== 'all' || searchQuery ? ` sur ${products.length} au total` : ''}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ORDERS */}
          {tab === 'orders' && (
            <div className="space-y-4">
              {/* Cartes statuts commandes */}
              <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
                {([
                  { key: 'all',       label: 'Toutes',     dot: 'bg-navy',          active: 'bg-navy text-white',            inactive: 'bg-white text-navy border border-gray-100' },
                  { key: 'pending',   label: 'En attente', dot: 'bg-amber-400',     active: 'bg-amber-400 text-white',       inactive: 'bg-white text-amber-600 border border-gray-100' },
                  { key: 'confirmed', label: 'Confirmées', dot: 'bg-teal-600',      active: 'bg-teal-600 text-white',        inactive: 'bg-white text-teal-700 border border-gray-100' },
                  { key: 'shipped',   label: 'Expédiées',  dot: 'bg-gold',          active: 'bg-gold text-white',            inactive: 'bg-white text-gold-dark border border-gray-100' },
                  { key: 'delivered', label: 'Livrées',    dot: 'bg-emerald-500',   active: 'bg-emerald-500 text-white',     inactive: 'bg-white text-emerald-600 border border-gray-100' },
                  { key: 'cancelled', label: 'Annulées',   dot: 'bg-gray-400',      active: 'bg-gray-500 text-white',        inactive: 'bg-white text-gray-500 border border-gray-100' },
                ] as const).map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setOrderStatusFilter(s.key)}
                    className={`rounded-xl px-3 py-3 text-left transition-all cursor-pointer ${orderStatusFilter === s.key ? s.active : s.inactive}`}
                  >
                    <p className="text-xl font-bold leading-tight">{orderCounts[s.key]}</p>
                    <p className={`text-xs mt-0.5 whitespace-nowrap ${orderStatusFilter === s.key ? 'text-white/80' : 'text-gray-500'}`}>{s.label}</p>
                  </button>
                ))}
              </div>

              <div className="bg-white rounded-xl border border-gray-100">
                {/* Barre de recherche */}
                <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
                  <div className="flex-1 relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                      <i className="ri-search-line text-sm"></i>
                    </div>
                    <input
                      value={orderSearch}
                      onChange={(e) => setOrderSearch(e.target.value)}
                      placeholder="Rechercher par produit, acheteur ou n° commande..."
                      className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                    />
                    {orderSearch && (
                      <button
                        onClick={() => setOrderSearch('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    )}
                  </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-50">
                        {['#','Produit','Acheteur','Qté','Total','Statut','Date'].map(h =>
                          <th key={h} className="text-left text-xs font-semibold text-gray-400 px-5 py-3">{h}</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-5 py-12 text-center">
                            <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
                              <i className="ri-shopping-bag-line text-gray-400 text-xl"></i>
                            </div>
                            <p className="text-sm font-semibold text-gray-500">Aucune commande trouvée</p>
                            <p className="text-xs text-gray-400 mt-1">Essayez un autre terme ou changez le filtre</p>
                          </td>
                        </tr>
                      ) : (
                        filteredOrders.map((o) => (
                          <tr key={o.id} onClick={() => setSelectedOrder(o)} className="border-b border-gray-50 hover:bg-gold-50/30 transition-colors cursor-pointer">
                            <td className="px-5 py-3 text-xs text-gold-dark font-mono font-semibold">{o.id}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                        <img src={o.productImage} alt="" onError={handleProductImageError} className="w-8 h-8 rounded-lg object-cover bg-gray-100 flex-shrink-0"/>
                                <span className="text-sm text-gray-900 truncate max-w-[130px]">{
                                  orderSearch
                                    ? o.productName.split(new RegExp(`(${orderSearch})`, 'gi')).map((part, i) =>
                                        part.toLowerCase() === orderSearch.toLowerCase()
                                          ? <mark key={i} className="bg-gold-100 text-gold-dark rounded px-0.5">{part}</mark>
                                          : part
                                      )
                                    : o.productName
                                }</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{
                              orderSearch
                                ? o.buyer.split(new RegExp(`(${orderSearch})`, 'gi')).map((part, i) =>
                                    part.toLowerCase() === orderSearch.toLowerCase()
                                      ? <mark key={i} className="bg-gold-100 text-gold-dark rounded px-0.5">{part}</mark>
                                      : part
                                  )
                                : o.buyer
                            }</td>
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900">{o.quantity}</td>
                            <td className="px-4 py-3 text-sm font-bold text-gray-900">{formatPrice(o.total)}</td>
                            <td className="px-4 py-3">
                              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${orderDisplayStatus(o).color}`}>
                                {orderDisplayStatus(o).label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{formatDate(o.date)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                {filteredOrders.length > 0 && (
                  <div className="px-5 py-3 border-t border-gray-50">
                    <p className="text-xs text-gray-400">
                      {filteredOrders.length} commande{filteredOrders.length > 1 ? 's' : ''} affichée{filteredOrders.length > 1 ? 's' : ''}
                      {orderStatusFilter !== 'all' || orderSearch ? ` sur ${orders.length} au total` : ''}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* NOTIFICATIONS */}
          {tab === 'notifications' && (
            <div className="bg-white rounded-xl border border-gray-100">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-gray-900">Notifications</h3>
                  {vendorUnreadCount > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{vendorUnreadCount}</span>
                  )}
                </div>
                {vendorUnreadCount > 0 && (
                  <button onClick={() => { markVendorAllRead(); saveLastSeenDate('vendor'); }} className="text-xs text-gold hover:text-gold-dark cursor-pointer whitespace-nowrap">
                    Tout marquer comme lu
                  </button>
                )}
              </div>
              <div className="divide-y divide-gray-50">
                {vendorNotifs.map((n) => {
                  const iconMap: Record<string,string> = {
                    order: 'ri-shopping-bag-line',
                    order_paid: 'ri-bank-card-line',
                    order_payment_started: 'ri-time-line',
                    product_review: 'ri-star-smile-line',
                    approval: 'ri-checkbox-circle-line',
                    rejection: 'ri-close-circle-line',
                    reservation: 'ri-ticket-line',
                    admin_activity: 'ri-notification-3-line',
                  };
                  const colorMap: Record<string,string> = {
                    order: 'text-orange-500 bg-orange-50',
                    order_paid: 'text-emerald-500 bg-emerald-50',
                    order_payment_started: 'text-amber-600 bg-amber-50',
                    product_review: 'text-gold-dark bg-gold-100',
                    approval: 'text-emerald-500 bg-emerald-50',
                    rejection: 'text-red-500 bg-red-50',
                    reservation: 'text-violet-500 bg-violet-50',
                    admin_activity: 'text-blue-500 bg-blue-50',
                  };
                  return (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => {
                        markVendorAsRead(n.id);
                        setSelectedNotification({ ...n, read: true });
                      }}
                      className={`flex w-full items-start gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors ${!n.read ? 'bg-orange-50/30' : ''}`}
                    >
                      <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${colorMap[n.type] ?? 'text-gray-500 bg-gray-50'}`}><i className={`${iconMap[n.type] ?? 'ri-notification-line'} text-base`}></i></div>
                      <div className="flex-1">
                        <p className={`text-sm font-semibold ${!n.read ? 'text-gray-900' : 'text-gray-600'}`}>{n.title}</p>
                        <p className="text-sm text-gray-500 mt-0.5 whitespace-pre-line break-words">{n.message}</p>
                        <p className="text-xs text-gray-400 mt-1">{n.time}</p>
                      </div>
                      {!n.read && <div className="w-2 h-2 bg-gold rounded-full mt-2 flex-shrink-0"></div>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ADD PRODUCT — Multi-step form */}
          {tab === 'add' && (
            <div className="max-w-2xl mx-auto">
              {submitted ? (
                <div className="bg-white rounded-2xl p-10 border border-gray-100 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4"><i className="ri-checkbox-circle-line text-emerald-600 text-3xl"></i></div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Produit soumis avec succès !</h3>
                  <p className="text-sm text-gray-500">Votre produit est en cours de validation par l'administrateur.<br/>Vous serez notifié dès qu'il sera approuvé.</p>
                  <span className="inline-flex items-center gap-1.5 mt-4 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold"><i className="ri-time-line text-xs"></i>🟡 En attente de validation</span>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  {/* Steps */}
                  <div className="px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      {[{ n: 1, l: 'Informations' }, { n: 2, l: 'Livraison' }, { n: 3, l: 'Aperçu' }].map((s, i, arr) => (
                        <div key={s.n} className="flex items-center gap-2 flex-1">
                          <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${step >= s.n ? 'bg-gold text-white' : 'bg-gray-100 text-gray-400'}`}>{step > s.n ? <i className="ri-check-line text-xs"></i> : s.n}</div>
                          <span className={`text-xs font-medium whitespace-nowrap ${step >= s.n ? 'text-gray-900' : 'text-gray-400'}`}>{s.l}</span>
                          {i < arr.length - 1 && <div className={`flex-1 h-0.5 ${step > s.n ? 'bg-gold' : 'bg-gray-100'}`}></div>}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="p-6 space-y-4">
                    {step === 1 && (
                      <>
                        <h3 className="text-sm font-bold text-gray-900 mb-2">Informations du produit</h3>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Nom du produit *</label>
                          <input value={form.name} onChange={e=>setField('name',e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Ex: Robe Wax Premium Colorée" />
                          {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Catégorie *</label>
                          <select value={form.category} onChange={e=>setField('category',e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold">
                            <option value="">Choisir une catégorie</option>
                            {PRODUCT_CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
                          </select>
                          {errors.category && <p className="text-xs text-red-500 mt-1">{errors.category}</p>}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Prix (FCFA) *</label>
                            <input type="number" value={form.price} onChange={e=>setField('price',e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Ex: 15000" />
                            {errors.price && <p className="text-xs text-red-500 mt-1">{errors.price}</p>}
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Stock disponible *</label>
                            <input type="number" value={form.stock} onChange={e=>setField('stock',e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Ex: 10" />
                            {errors.stock && <p className="text-xs text-red-500 mt-1">{errors.stock}</p>}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">État du produit *</label>
                          <div className="flex gap-3">
                            {['Neuf','Occasion'].map(c=>(
                              <button key={c} onClick={()=>setField('condition',c)} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all cursor-pointer whitespace-nowrap ${form.condition===c?'border-gold bg-gold-50 text-gold-dark':'border-gray-200 text-gray-500 hover:border-gray-300'}`}>{c}</button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Étiquette produit</label>
                          <select value={form.badge} onChange={e=>setField('badge',e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold">
                            <option value="">Aucune étiquette</option>
                            {PRODUCT_BADGES.map((badge) => <option key={badge} value={badge}>{badge}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Description *</label>
                          <textarea value={form.description} onChange={e=>setField('description',e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none h-24" placeholder="Décrivez votre produit..." maxLength={500} />
                          <div className="flex justify-between items-center mt-1">
                            {errors.description ? <p className="text-xs text-red-500">{errors.description}</p> : <span/>}
                            <p className="text-xs text-gray-400">{form.description.length}/500</p>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Images du produit (min 1, max 5)</label>
                          <div className="flex items-center gap-3 flex-wrap">
                            {imagePreviews.map((img, i) => (
                              <div key={i} className="w-20 h-20 rounded-xl overflow-hidden border border-gray-200 relative group flex-shrink-0">
                                <img src={img} alt="" className="w-full h-full object-cover" />
                                <button
                                  type="button"
                                  onClick={() => removeImage(i)}
                                  className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                                >
                                  <i className="ri-delete-bin-line text-white text-sm"></i>
                                </button>
                              </div>
                            ))}
                            {imagePreviews.length < 5 && (
                              <>
                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  className="hidden"
                                  onChange={(e) => handleImageFiles(e.target.files)}
                                />
                                <button
                                  type="button"
                                  onClick={() => fileInputRef.current?.click()}
                                  className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-emerald-400 hover:text-emerald-500 transition-all cursor-pointer flex-shrink-0"
                                >
                                  <i className="ri-add-line text-xl"></i>
                                  <span className="text-[10px] mt-0.5">Ajouter</span>
                                </button>
                              </>
                            )}
                          </div>
                          {imagePreviews.length === 0 && (
                            <p className="text-xs text-gray-400 mt-1">Aucune image sélectionnée — cliquez sur &quot;Ajouter&quot; pour choisir des photos depuis votre appareil.</p>
                          )}
                        </div>
                      </>
                    )}

                    {step === 2 && (
                      <>
                        <h3 className="text-sm font-bold text-gray-900 mb-2">Informations de livraison</h3>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Délai de livraison (optionnel)</label>
                          <input value={form.deliveryDelay} onChange={e=>setField('deliveryDelay',e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Ex: 2–3 jours" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Localisation du vendeur (optionnel)</label>
                          <select value={form.location} onChange={e=>setField('location',e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold">
                            <option value="">Choisir une commune de Dakar</option>
                            {DAKAR_COMMUNES.map(c=><option key={c} value={`${c}, Dakar`}>{c}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Poids / Taille (optionnel)</label>
                          <input value={form.weight} onChange={e=>setField('weight',e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Ex: 500g / Taille M" />
                        </div>
                      </>
                    )}

                    {step === 3 && (
                      <>
                        <h3 className="text-sm font-bold text-gray-900 mb-4">Aperçu de votre produit</h3>
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-3">
                          <div className="flex items-start gap-4">
                            {imagePreviews.length > 0 ? (
                              <img src={imagePreviews[0]} alt="" onError={handleProductImageError} className="w-24 h-24 rounded-xl object-cover bg-gray-200 flex-shrink-0" />
                            ) : (
                              <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <i className="ri-image-line text-gray-300 text-3xl"></i>
                              </div>
                            )}
                            <div>
                              <p className="text-base font-bold text-gray-900">{form.name || 'Nom du produit'}</p>
                              <p className="text-sm text-gray-500">{form.category || 'Catégorie'}</p>
                              {form.badge ? <p className="inline-flex mt-2 px-2.5 py-1 rounded-full text-xs font-bold bg-gold-100 text-gold-dark">{form.badge}</p> : null}
                              <p className="text-lg font-bold text-emerald-600 mt-1">{form.price ? formatPrice(Number(form.price)) : '—'}</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            {[
                              { l: 'Stock', v: form.stock || '—' }, { l: 'État', v: form.condition },
                              { l: 'Livraison', v: form.deliveryDelay || '—' }, { l: 'Lieu', v: form.location || '—' },
                              { l: 'Poids/Taille', v: form.weight || '—' },
                            ].map(item => (
                              <div key={item.l} className="bg-white rounded-lg p-2.5">
                                <p className="text-xs text-gray-400">{item.l}</p>
                                <p className="text-sm font-semibold text-gray-900">{item.v}</p>
                              </div>
                            ))}
                          </div>
                          <div className="bg-white rounded-lg p-3">
                            <p className="text-xs text-gray-400 mb-1">Description</p>
                            <p className="text-sm text-gray-700">{form.description || '—'}</p>
                          </div>
                          <div className="flex items-center gap-2 bg-amber-50 p-3 rounded-xl">
                            <i className="ri-information-line text-amber-600"></i>
                            <p className="text-xs text-amber-700">Après soumission, votre produit sera examiné par l'admin avant publication.</p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="px-6 pb-6 flex items-center gap-3">
                    {step > 1 && <button onClick={() => setStep((s) => (s - 1) as Step)} className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">← Retour</button>}
                    <div className="flex-1"></div>
                    {step < 3
                      ? <button onClick={handleNext} className="flex items-center gap-1.5 px-6 py-2.5 bg-navy text-white rounded-xl text-sm font-semibold hover:bg-navy-lighter cursor-pointer whitespace-nowrap">Continuer →</button>
                      : <button onClick={handleSubmit} className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-gold to-gold-dark text-white rounded-xl text-sm font-semibold hover:from-gold-dark hover:to-[#a07828] cursor-pointer whitespace-nowrap"><i className="ri-send-plane-line"></i>Soumettre pour validation</button>
                    }
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ORDER DETAIL PANEL */}
      {selectedOrder && (() => {
        const statusSteps: { key: string; label: string; icon: string; color: string }[] = [
          { key: 'pending',   label: 'Commande reçue',     icon: 'ri-time-line',            color: 'text-amber-600 bg-amber-50' },
          { key: 'confirmed', label: 'Commande confirmée', icon: 'ri-checkbox-circle-line', color: 'text-teal-600 bg-teal-50' },
          { key: 'shipped',   label: 'Colis expédié',      icon: 'ri-truck-line',           color: 'text-navy bg-navy/10' },
          { key: 'delivered', label: 'Livré au client',    icon: 'ri-map-pin-2-line',       color: 'text-emerald-600 bg-emerald-50' },
        ];
        const order = selectedOrder;
        const statusOrder = ['pending','confirmed','shipped','delivered'];
        const currentIdx = statusOrder.indexOf(order.status);
        const isCancelled = order.status === 'cancelled';

        const mockDates: Record<string, string> = {
          pending:   order.date,
          confirmed: new Date(new Date(order.date).getTime() + 1*3600000).toISOString(),
          shipped:   new Date(new Date(order.date).getTime() + 24*3600000).toISOString(),
          delivered: new Date(new Date(order.date).getTime() + 72*3600000).toISOString(),
        };

        return (
          <>
            {/* Overlay */}
            <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setSelectedOrder(null)} />
            {/* Panel */}
            <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
                <div>
                  <p className="text-xs text-gray-400 font-mono mb-0.5">{order.id}</p>
                  <h3 className="text-sm font-bold text-navy">Détail de la commande</h3>
                </div>
                <button onClick={() => setSelectedOrder(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                {/* Produit */}
                <div className="flex items-center gap-4 bg-cream rounded-xl p-4">
                  <img src={order.productImage} alt={order.productName} onError={handleProductImageError} className="w-16 h-16 rounded-xl object-cover bg-gray-200 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">{order.productName}</p>
                    <p className="text-xs text-gray-500 mt-1">Quantité : <span className="font-semibold text-gray-800">{order.quantity}</span></p>
                    <p className="text-base font-bold text-navy mt-1">{formatPrice(order.total)}</p>
                  </div>
                </div>

                {/* Statut actuel */}
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Statut actuel</p>
                  <span className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full ${orderDisplayStatus(order).color}`}>
                    {orderDisplayStatus(order).label}
                  </span>
                  {order.paymentStatus && order.paymentStatus !== 'completed' ? (
                    <p className="mt-2 text-xs font-medium text-amber-600">Le client a démarré le paiement, mais il n'a pas encore été validé.</p>
                  ) : null}
                </div>

                {/* Adresse de livraison */}
                <div className="bg-cream rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 flex items-center justify-center bg-navy/10 rounded-lg">
                      <i className="ri-map-pin-line text-navy text-sm"></i>
                    </div>
                    <p className="text-xs font-bold text-gray-700">Adresse de livraison</p>
                  </div>
                  <p className="text-sm text-gray-800 font-medium">{order.address}</p>
                  <p className="text-xs text-gray-400 mt-1">Client : {order.buyer}</p>
                  <p className="text-xs text-gray-400 mt-1">Téléphone : {order.phone || 'Non renseigné'}</p>
                </div>

                {/* Historique statut */}
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Historique du suivi</p>
                  {isCancelled ? (
                    <div className="flex items-start gap-3 py-3 px-4 bg-red-50 rounded-xl">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100 flex-shrink-0">
                        <i className="ri-close-circle-line text-red-500 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-red-700">Commande annulée</p>
                        <p className="text-xs text-red-400 mt-0.5">{formatDate(order.date)}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      {/* Ligne verticale */}
                      <div className="absolute left-[15px] top-4 bottom-4 w-0.5 bg-gray-100"></div>
                      <div className="space-y-1">
                        {statusSteps.map((step, idx) => {
                          const done = idx <= currentIdx;
                          const current = idx === currentIdx;
                          return (
                            <div key={step.key} className={`flex items-start gap-3 py-2.5 px-3 rounded-xl transition-colors ${current ? 'bg-gold-50/60' : ''}`}>
                              <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 z-10 ${done ? step.color : 'bg-gray-100 text-gray-300'}`}>
                                <i className={`${step.icon} text-sm`}></i>
                              </div>
                              <div className="flex-1 min-w-0 pt-0.5">
                                <p className={`text-sm font-semibold ${done ? 'text-gray-900' : 'text-gray-300'}`}>{step.label}</p>
                                {done && (
                                  <p className="text-xs text-gray-400 mt-0.5">{formatDate(mockDates[step.key])}</p>
                                )}
                                {current && (
                                  <span className="inline-block text-[10px] font-bold text-gold-dark bg-gold-100 px-1.5 py-0.5 rounded-full mt-1">Étape actuelle</span>
                                )}
                              </div>
                              {current && (
                                <div className="w-2 h-2 bg-gold rounded-full mt-2 flex-shrink-0 animate-pulse"></div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Infos complémentaires */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Date commande', value: formatDate(order.date), icon: 'ri-calendar-line' },
                    { label: 'Montant total',  value: formatPrice(order.total), icon: 'ri-money-cny-circle-line' },
                  ].map(item => (
                    <div key={item.label} className="bg-cream rounded-xl p-3 flex items-start gap-2">
                      <div className="w-6 h-6 flex items-center justify-center text-gold-dark flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-sm`}></i>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">{item.label}</p>
                        <p className="text-xs font-bold text-gray-800 mt-0.5">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer actions */}
              <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0 space-y-2">
                {(() => {
                  if (order.paymentStatus && order.paymentStatus !== 'completed') {
                    return (
                      <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-50 text-amber-700 text-sm font-semibold">
                        <i className="ri-error-warning-line text-base"></i>
                        Paiement non validé — aucune action de livraison possible
                      </div>
                    );
                  }

                  const nextStatuses = getNextStatuses(order.status);
                  if (nextStatuses.length === 0) {
                    return (
                      <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold ${order.status === 'delivered' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                        <i className={`${order.status === 'delivered' ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'} text-base`}></i>
                        {order.status === 'delivered' ? 'Commande finalisée — aucune action possible' : 'Commande annulée — aucune action possible'}
                      </div>
                    );
                  }
                  return (
                    <>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Livraison</p>
                      <div className="flex gap-2">
                        {nextStatuses.map((s) => (
                          <button
                            key={s.key}
                            onClick={() => handleUpdateOrderStatus(s.key)}
                            disabled={statusUpdateLoading}
                            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 ${s.color}`}
                          >
                            {statusUpdateLoading
                              ? <i className="ri-loader-4-line animate-spin text-base"></i>
                              : <i className={`${s.icon} text-base`}></i>
                            }
                            {s.label}
                          </button>
                        ))}
                      </div>
                      <button onClick={() => setSelectedOrder(null)} className="w-full py-2 text-xs text-gray-400 hover:text-gray-600 cursor-pointer">
                        Fermer
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>
          </>
        );
      })()}

      {/* DELETE CONFIRMATION MODAL */}
          {deleteConfirmId && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
                <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
                  <i className="ri-delete-bin-line text-red-500 text-xl"></i>
                </div>
                <h3 className="text-base font-bold text-gray-900 text-center mb-2">Supprimer ce produit ?</h3>
                <p className="text-sm text-gray-500 text-center mb-5">Le produit sera déplacé dans la corbeille et ne sera plus visible dans l’application.</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setDeleteConfirmId(null)}
                    className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => void handleDeleteProduct(deleteConfirmId)}
                    className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 cursor-pointer whitespace-nowrap"
                  >
                    Mettre en corbeille
                  </button>
                </div>
              </div>
            </div>
          )}

      {/* TOAST NOTIFICATIONS */}
      <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg border animate-[slideIn_0.3s_ease-out] ${
              t.type === 'success'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : t.type === 'error'
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-white border-gray-200 text-gray-800'
            }`}
          >
            <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 ${
              t.type === 'success'
                ? 'bg-emerald-100 text-emerald-600'
                : t.type === 'error'
                ? 'bg-red-100 text-red-600'
                : 'bg-gray-100 text-gray-500'
            }`}
>
              <i className={`${
                t.type === 'success' ? 'ri-checkbox-circle-line' : t.type === 'error' ? 'ri-close-circle-line' : 'ri-information-line'
              } text-sm`}></i>
            </div>
            <p className="text-sm font-medium">{t.message}</p>
          </div>
        ))}
      </div>

          {/* EDIT PRODUCT MODAL */}
          {editProduct && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
              <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-gold-50 rounded-lg">
                      <i className="ri-edit-line text-gold-dark text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-900">Modifier le produit</h3>
                      <p className="text-xs text-gray-400 truncate max-w-[200px]">{editProduct.name}</p>
                    </div>
                  </div>
                  <button onClick={() => setEditProduct(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
                    <i className="ri-close-line text-lg"></i>
                  </button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
                  <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg flex items-center gap-1.5">
                    <i className="ri-information-line"></i>
                    Toute modification repassera le produit en validation.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Nom du produit *</label>
                      <input value={editForm.name ?? ''} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
                      {editErrors.name && <p className="text-xs text-red-500 mt-1">{editErrors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Catégorie *</label>
                      <select value={editForm.category ?? ''} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold">
                        <option value="">Catégorie</option>
                        {PRODUCT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      {editErrors.category && <p className="text-xs text-red-500 mt-1">{editErrors.category}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">État *</label>
                      <div className="flex gap-2">
                        {['Neuf', 'Occasion'].map(c => (
                          <button key={c} type="button" onClick={() => setEditForm(f => ({ ...f, condition: c }))}
                            className={`flex-1 py-2.5 rounded-xl text-xs font-semibold border-2 transition-all cursor-pointer whitespace-nowrap ${editForm.condition === c ? 'border-gold bg-gold-50 text-gold-dark' : 'border-gray-200 text-gray-500'}`}>{c}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Étiquette produit</label>
                      <select value={editForm.badge ?? ''} onChange={e => setEditForm(f => ({ ...f, badge: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold">
                        <option value="">Aucune étiquette</option>
                        {PRODUCT_BADGES.map((badge) => <option key={badge} value={badge}>{badge}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Prix (FCFA) *</label>
                      <input type="number" value={editForm.price ?? ''} onChange={e => setEditForm(f => ({ ...f, price: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
                      {editErrors.price && <p className="text-xs text-red-500 mt-1">{editErrors.price}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Stock disponible *</label>
                      <input type="number" value={editForm.stock ?? ''} onChange={e => setEditForm(f => ({ ...f, stock: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
                      {editErrors.stock && <p className="text-xs text-red-500 mt-1">{editErrors.stock}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Délai de livraison (optionnel)</label>
                      <input value={editForm.deliveryDelay ?? ''} onChange={e => setEditForm(f => ({ ...f, deliveryDelay: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Ex: 2–3 jours" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Localisation du vendeur (optionnel)</label>
                      <select value={editForm.location ?? ''} onChange={e => setEditForm(f => ({ ...f, location: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold">
                        <option value="">Commune de Dakar</option>
                        {DAKAR_COMMUNES.map(c => <option key={c} value={`${c}, Dakar`}>{c}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Description *</label>
                      <textarea value={editForm.description ?? ''} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none h-24" maxLength={500} />
                      <div className="flex justify-between items-center mt-1">
                        {editErrors.description ? <p className="text-xs text-red-500">{editErrors.description}</p> : <span />}
                        <p className="text-xs text-gray-400">{(editForm.description ?? '').length}/500</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-100 flex items-center gap-3 flex-shrink-0">
                  <button onClick={() => setEditProduct(null)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                    Annuler
                  </button>
                  <button onClick={handleSaveEdit} className="flex-1 py-2.5 bg-gradient-to-r from-gold to-gold-dark text-white rounded-xl text-sm font-semibold hover:from-gold-dark hover:to-[#a07828] cursor-pointer whitespace-nowrap">
                    Enregistrer les modifications
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* PROFILE */}
          {tab === 'profile' && (
            <div className="max-w-3xl mx-auto space-y-5">

              {/* Avatar + identité */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <div className="flex items-center gap-5">
                  <div className="relative flex-shrink-0">
                    <img
                      src={profile.avatarPreview || user?.avatar || PRODUCT_FALLBACK_IMAGE}
                      alt={profile.name}
                      className="w-20 h-20 rounded-2xl object-cover bg-gray-100 border-2 border-gold/30"
                    />
                    <button
                      onClick={() => profileFileRef.current?.click()}
                      className="absolute -bottom-1.5 -right-1.5 w-7 h-7 flex items-center justify-center bg-gold rounded-full cursor-pointer hover:bg-gold-dark transition-colors"
                    >
                      <i className="ri-camera-line text-white text-xs"></i>
                    </button>
                    <input
                      ref={profileFileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => handleProfileAvatar(e.target.files)}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-bold text-navy">{profile.name}</h2>
                    <p className="text-sm text-gray-500">{profile.shopName}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                        <i className="ri-store-2-line text-xs"></i>Vendeur actif
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded-full">
                        <i className="ri-map-pin-line text-xs"></i>{profile.location}
                      </span>
                    </div>
                  </div>
                  {/* Mini stats */}
                  <div className="hidden lg:flex items-center gap-6 flex-shrink-0">
                    {[
                      { label: 'Produits', value: products.length, icon: 'ri-store-line' },
                      { label: 'Commandes', value: orders.length, icon: 'ri-shopping-bag-line' },
                      { label: 'Approbation', value: `${approvalRate}%`, icon: 'ri-star-line' },
                    ].map(s => (
                      <div key={s.label} className="text-center">
                        <p className="text-lg font-bold text-navy">{s.value}</p>
                        <p className="text-xs text-gray-400">{s.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Informations personnelles */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 flex items-center justify-center bg-navy/10 rounded-lg">
                    <i className="ri-user-line text-navy text-sm"></i>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">Informations personnelles</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Nom complet *</label>
                    <input value={profile.name} onChange={e => setProfileField('name', e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
                    {profileErrors.name && <p className="text-xs text-red-500 mt-1">{profileErrors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Adresse e-mail *</label>
                    <input type="email" value={profile.email} onChange={e => setProfileField('email', e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
                    {profileErrors.email && <p className="text-xs text-red-500 mt-1">{profileErrors.email}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Téléphone *</label>
                    <input value={profile.phone} onChange={e => setProfileField('phone', e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                      placeholder="+221 77 xxx xx xx" />
                    {profileErrors.phone && <p className="text-xs text-red-500 mt-1">{profileErrors.phone}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Localisation</label>
                    <select value={profile.location} onChange={e => setProfileField('location', e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold">
                      <option value="">Commune de Dakar</option>
                      {DAKAR_COMMUNES.map(c => <option key={c} value={`${c}, Dakar`}>{c}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Infos boutique */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 flex items-center justify-center bg-gold-50 rounded-lg">
                    <i className="ri-store-2-line text-gold-dark text-sm"></i>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">Ma boutique</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Nom de la boutique *</label>
                    <input value={profile.shopName} onChange={e => setProfileField('shopName', e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold" />
                    {profileErrors.shopName && <p className="text-xs text-red-500 mt-1">{profileErrors.shopName}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Catégorie principale</label>
                    <select value={profile.category} onChange={e => setProfileField('category', e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold">
                      <option value="">Non renseignée</option>
                      {PRODUCT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Description / Biographie</label>
                    <textarea value={profile.bio} onChange={e => setProfileField('bio', e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none h-24"
                      placeholder="Décrivez votre boutique et votre activité..." maxLength={300} />
                    <p className="text-xs text-gray-400 text-right mt-1">{profile.bio.length}/300</p>
                  </div>
                </div>
              </div>

              {/* Bouton sauvegarder */}
              <div className="flex items-center justify-end gap-3 pb-4">
                <button
                  onClick={() => {
                    setProfile(profileFromUser(user));
                    setProfileAvatarFile(null);
                    setProfileErrors({});
                    addToast('Modifications annulées', 'info');
                  }}
                  className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={profileSaving}
                  className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-gold to-gold-dark text-white rounded-xl text-sm font-semibold hover:from-gold-dark hover:to-[#a07828] disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {profileSaving
                    ? <><i className="ri-loader-4-line animate-spin"></i>Enregistrement...</>
                    : <><i className="ri-save-line"></i>Enregistrer le profil</>
                  }
                </button>
              </div>

            </div>
          )}

          {selectedNotification ? (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedNotification(null)}>
              <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-gold-dark">Notification</p>
                    <h3 className="mt-1 text-lg font-bold text-navy">{selectedNotification.title}</h3>
                    <p className="mt-1 text-xs text-gray-400">{selectedNotification.time}</p>
                  </div>
                  <button type="button" onClick={() => setSelectedNotification(null)} className="rounded-xl p-2 text-gray-400 hover:bg-gray-100">
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>
                <p className="mt-5 whitespace-pre-line break-words text-sm leading-7 text-gray-600">{selectedNotification.message}</p>
                <button type="button" onClick={() => setSelectedNotification(null)} className="mt-6 w-full rounded-xl bg-navy py-3 text-sm font-bold text-white">
                  Fermer
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
